"use client";

import { makeFunctionReference } from "convex/server";
import { useMutation, useQuery } from "convex/react";
import { QuerySkeleton } from "@/components/ui/skeleton";
import { ConsoleDashboard } from "./dashboard";
import type { ApprovalSource } from "@/core/approvals/unified-approvals";
import type { getConsoleSnapshot } from "@/lib/demo-data";

type Snapshot = ReturnType<typeof getConsoleSnapshot>;

const getDashboardSnapshot = makeFunctionReference<"query", { tenantId: string; limit?: number }, Snapshot>(
  "dashboard:getSnapshot",
);

// decidedBy is server-derived from the authenticated membership; clients no
// longer send an actor id at all.
const decideApproval = makeFunctionReference<
  "mutation",
  { tenantId: string; id: string; source: ApprovalSource; status: string },
  unknown
>("approvals:decideUnified");

const currentActor = makeFunctionReference<
  "query",
  { tenantId: string },
  { userId: string; role: string } | null
>("approvals:currentActor");

export function ConsoleDashboardClient({
  tenantId,
  fallback,
}: {
  tenantId: string;
  fallback: Snapshot;
}) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) return <ConsoleDashboard tenantId={tenantId} snapshot={fallback} />;
  return <LiveConsoleDashboard tenantId={tenantId} fallback={fallback} />;
}

function LiveConsoleDashboard({ tenantId, fallback }: { tenantId: string; fallback: Snapshot }) {
  const snapshot = useQuery(getDashboardSnapshot, { tenantId, limit: 50 });
  // undefined = resolving, null = unauthenticated / no membership. Fail closed:
  // never substitute a fabricated actor id.
  const actor = useQuery(currentActor, { tenantId });
  const decide = useMutation(decideApproval);
  if (snapshot === undefined) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-5">
        <QuerySkeleton surface="consoleDashboard" />
      </div>
    );
  }
  return (
    <ConsoleDashboard
      tenantId={tenantId}
      snapshot={snapshot ?? fallback}
      currentUserId={actor === undefined ? undefined : actor?.userId ?? null}
      onDecide={({ id, source, status }) => void decide({ tenantId, id, source, status })}
    />
  );
}
